/**
 * Core Tracker class with tracking methods
 */

import {
  ScriptConfig,
  BasePayload,
  EventPayload,
  PageviewPayload,
  CustomEventPayload,
  WebVitalsEventPayload,
  ITracker
} from './types';
import {
  getVisitorId,
  getSessionId,
  shouldSkipUrl,
  isExternalLink,
  debugLog,
  logWarning,
  logError,
  validateDomain
} from './utils';
import { WebVitalsCollector, WebVitalsData } from './webVitals';

export class Tracker implements ITracker {
  private config: ScriptConfig;
  private webVitalsCollector: WebVitalsCollector | null = null;
  private currentWebVitals: WebVitalsData | null = null;
  private domainValidated: boolean = false;
  private trackingEnabled: boolean = false;

  constructor(config: ScriptConfig) {
    this.config = config;

    const domainValidation = validateDomain(config.domain);
    this.domainValidated = domainValidation.isValid;
    debugLog(`Domain validation: ${this.domainValidated}`, undefined, config.debugMode);

    this.initializeWebVitals();
    this.trackingEnabled = true;
    debugLog('Tracker initialised', config, config.debugMode);
  }

  /**
   * Initialize Web Vitals collection
   */
  private initializeWebVitals(): void {
    try {
      this.webVitalsCollector = new WebVitalsCollector((data: WebVitalsData) => {
        this.currentWebVitals = data;
        this.trackWebVitals(data);
      });
      this.webVitalsCollector.initialize();
    } catch (error) {
      debugLog('Failed to initialize Web Vitals', error, this.config.debugMode);
    }
  }

  /**
   * Safely stringify data with circular reference handling
   */
  private safeStringify(data: any): string {
    const seen = new WeakSet();
    return JSON.stringify(data, (_key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      return value;
    });
  }

  private parseHostname(url: string): string {
    try {
      return new URL(url).hostname || '';
    } catch {
      return '';
    }
  }

  private parsePathname(url: string): string {
    try {
      return new URL(url).pathname || '/';
    } catch {
      return '/';
    }
  }

  private parseQuerystring(url: string): string | null {
    try {
      const urlObj = new URL(url);
      return urlObj.search ? urlObj.search.substring(1) : null;
    } catch {
      return null;
    }
  }

  private parseUrlParameters(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const params: Record<string, string> = {};
      urlObj.searchParams.forEach((value, key) => {
        params[key] = value;
      });
      return Object.keys(params).length > 0 ? JSON.stringify(params) : null;
    } catch {
      return null;
    }
  }

  /**
   * Classify traffic channel based on referrer and URL
   */
  private classifyTrafficChannel(referrer: string | null, url: string): string {
    if (!referrer) {
      return 'direct';
    }

    try {
      const referrerHost = new URL(referrer).hostname.toLowerCase();
      const currentHost = new URL(url).hostname.toLowerCase();

      if (referrerHost === currentHost) {
        return 'internal';
      }

      if (referrerHost.includes('google.') || referrerHost.includes('bing.') ||
          referrerHost.includes('yahoo.') || referrerHost.includes('duckduckgo.') ||
          referrerHost.includes('baidu.') || referrerHost.includes('yandex.')) {
        return 'search';
      }

      if (referrerHost.includes('facebook.') || referrerHost.includes('twitter.') ||
          referrerHost.includes('linkedin.') || referrerHost.includes('instagram.') ||
          referrerHost.includes('youtube.') || referrerHost.includes('tiktok.') ||
          referrerHost.includes('reddit.') || referrerHost.includes('pinterest.')) {
        return 'social';
      }

      const urlObj = new URL(url);
      if (urlObj.searchParams.has('utm_source') || urlObj.searchParams.has('utm_campaign') ||
          urlObj.searchParams.has('utm_medium') || urlObj.searchParams.has('gclid') ||
          urlObj.searchParams.has('fbclid')) {
        return 'campaign';
      }

      return 'referral';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Collect minimal base data for all events - heavy parsing happens on the server
   */
  private collectBaseData(): BasePayload {
    const safeAccess = <T>(fn: () => T, fallback: T): T => {
      try {
        return fn();
      } catch (error) {
        debugLog(`Error accessing property: ${error}`, error, this.config.debugMode);
        return fallback;
      }
    };

    const href = safeAccess(() => window.location.href, 'unknown');
    const referrer = safeAccess(() => document.referrer || null, null);
    const pageTitle = safeAccess(() => document.title || 'Untitled', 'Untitled');

    return {
      websiteId: this.config.websiteId,
      domain: this.config.domain,
      href,
      referrer,
      viewport: {
        width: safeAccess(() => window.innerWidth, 0),
        height: safeAccess(() => window.innerHeight, 0)
      },
      screen: {
        width: safeAccess(() => window.screen?.width, 0),
        height: safeAccess(() => window.screen?.height, 0)
      },
      language: safeAccess(() => navigator?.language, 'unknown'),
      timezone: safeAccess(() => Intl.DateTimeFormat().resolvedOptions().timeZone, 'unknown'),
      userAgent: safeAccess(() => navigator?.userAgent, 'unknown'),
      visitorId: safeAccess(() => getVisitorId(), 'unknown'),
      sessionId: safeAccess(() => getSessionId(), 'unknown'),
      timestamp: Date.now(),
      pageTitle,
      webVitals: this.currentWebVitals
    };
  }

  /**
   * Build the flat payload accepted by /api/v1/push (see trakr-script/schema.ts)
   */
  private buildApiPayload(eventData: EventPayload) {
    const eventType = eventData.eventType || 'pageview';
    const eventName = eventData.eventName || eventType;
    const vitals = eventData.webVitals;

    return {
      timestamp: new Date(eventData.timestamp).toISOString(),
      website_id: eventData.websiteId,
      session_id: eventData.sessionId,
      visitor_id: eventData.visitorId,
      url: eventData.href,
      referrer: eventData.referrer,

      screen_width: eventData.screen?.width || 0,
      screen_height: eventData.screen?.height || 0,

      event_type: eventType,
      event_name: eventName,
      event_props: eventData.eventData && Object.keys(eventData.eventData).length > 0
        ? this.safeStringify(eventData.eventData)
        : null,

      hostname: this.parseHostname(eventData.href),
      pathname: this.parsePathname(eventData.href),
      querystring: this.parseQuerystring(eventData.href),
      url_parameters: this.parseUrlParameters(eventData.href),

      page_title: eventData.pageTitle || null,
      language: eventData.language || null,

      channel: this.classifyTrafficChannel(eventData.referrer, eventData.href),

      lcp: vitals?.lcp ?? null,
      cls: vitals?.cls ?? null,
      inp: vitals?.inp ?? null,
      fcp: vitals?.fcp ?? null,
      ttfb: vitals?.ttfb ?? null,

      // Enhanced server-side from user_agent
      browser: 'unknown',
      browser_version: 'unknown',
      os: 'unknown',
      os_version: 'unknown',
      device: 'unknown',

      // Enhanced server-side from the request
      country: null,
      region: null,
      city: null,
      coordinates: null,

      user_agent: eventData.userAgent
    };
  }

  /**
   * Send event data to server
   */
  private sendEvent(eventData: EventPayload, callback?: () => void): void {
    if (!this.trackingEnabled) {
      debugLog('Tracking disabled, skipping event', eventData, this.config.debugMode);
      callback?.();
      return;
    }

    if (shouldSkipUrl(eventData.href, this.config.skipPatterns)) {
      debugLog('URL matches skip pattern, skipping event', eventData.href, this.config.debugMode);
      callback?.();
      return;
    }

    let payload: string;
    try {
      payload = this.safeStringify(this.buildApiPayload(eventData));
    } catch (error) {
      logError('Failed to serialize event data', error);
      callback?.();
      return;
    }

    debugLog(`Sending ${eventData.eventType}`, payload, this.config.debugMode);

    const hasSendBeacon = typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function';
    if (hasSendBeacon) {
      try {
        if (navigator.sendBeacon(this.config.apiEndpoint, payload)) {
          debugLog('Event sent via sendBeacon', undefined, this.config.debugMode);
          callback?.();
          return;
        }
      } catch (error) {
        debugLog('sendBeacon failed, falling back to fetch', error, this.config.debugMode);
      }
    }

    if (typeof fetch === 'undefined') {
      logWarning('Neither sendBeacon nor fetch available, cannot send event');
      callback?.();
      return;
    }

    try {
      fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).then(response => {
        if (response.ok) {
          debugLog('Event sent via fetch', undefined, this.config.debugMode);
        } else {
          logWarning(`Failed to send event: ${response.status} ${response.statusText}`);
        }
      }).catch(error => {
        logError('Failed to send event via fetch', error);
      }).finally(() => {
        callback?.();
      });
    } catch (error) {
      logError('Failed to initiate fetch request', error);
      callback?.();
    }
  }

  /**
   * Track pageview
   */
  public trackPageview(callback?: () => void): void {
    if (!this.trackingEnabled) {
      callback?.();
      return;
    }

    const pageviewData: PageviewPayload = {
      ...this.collectBaseData(),
      eventType: 'pageview',
      eventName: 'pageview',
      title: document.title,
      path: window.location.pathname
    };

    this.sendEvent(pageviewData, callback);
  }

  /**
   * Track custom event. `eventName` is stored as event_name; event_type is always custom_event.
   */
  public trackCustomEvent(
    eventName: string,
    extraData?: Record<string, any>,
    callback?: () => void
  ): void {
    if (!this.trackingEnabled) {
      callback?.();
      return;
    }

    if (!eventName || typeof eventName !== 'string') {
      logError('Event type must be a non-empty string');
      callback?.();
      return;
    }

    const customEventData: CustomEventPayload = {
      ...this.collectBaseData(),
      eventType: 'custom_event',
      eventName: eventName.trim().slice(0, 128),
      eventData: extraData || {}
    };

    this.sendEvent(customEventData, callback);
  }

  /**
   * Track external link click
   */
  public trackExternalLink(linkElement: HTMLAnchorElement | null): void {
    if (!linkElement || !this.config.trackOutbound) {
      return;
    }

    const href = linkElement.href;
    if (!href || !isExternalLink(href)) {
      return;
    }

    this.trackCustomEvent('external_link', {
      url: href,
      text: linkElement.textContent?.trim() || '',
      target: linkElement.target || '_self'
    });
  }

  /**
   * Track Web Vitals metrics as a dedicated web_vitals event
   */
  trackWebVitals(vitals: WebVitalsData): void {
    if (!this.trackingEnabled) {
      return;
    }

    const payload: WebVitalsEventPayload = {
      ...this.collectBaseData(),
      eventType: 'web_vitals',
      eventName: 'web_vitals',
      webVitals: {
        lcp: vitals.lcp ?? null,
        cls: vitals.cls ?? null,
        inp: vitals.inp ?? null,
        fcp: vitals.fcp ?? null,
        ttfb: vitals.ttfb ?? null
      }
    };

    this.sendEvent(payload);
  }

  public isTrackingEnabled(): boolean {
    return this.trackingEnabled;
  }

  public disableTracking(): void {
    this.trackingEnabled = false;
    debugLog('Tracking disabled', undefined, this.config.debugMode);
  }

  public enableTracking(): void {
    this.trackingEnabled = true;
    debugLog('Tracking enabled', undefined, this.config.debugMode);
  }
}
